import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { RiskBadge } from "../components/RiskBadge";
import { PushToggle } from "../components/PushToggle";
import { STATUS_LABEL } from "../lib/constants";
import { FileText, PlusCircle, ChevronRight, Phone, MessageCircle, Star } from "lucide-react";
import { ReviewModal } from "../components/case/ReviewModal";
import { trackWhatsApp } from "../lib/beacon";

const MyCases = () => {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reviewing, setReviewing] = useState(null); // { caseObj, existing }

  useEffect(() => {
    api.get("/cases/mine").then(({ data }) => setCases(data)).finally(() => setLoading(false));
  }, []);

  const openReview = async (c) => {
    // busca avaliação existente para pré-preencher (edição)
    let existing = null;
    try {
      const { data } = await api.get(`/cases/${c.id}/review`);
      existing = data?.review || null;
    } catch { /* família ainda não avaliou */ }
    setReviewing({ caseObj: c, existing });
  };

  // WhatsApp de contato do RAF Direct (24h) — vem do env para permitir troca.
  const rafSupportWa = process.env.REACT_APP_SUPPORT_WA || "+5511996420785";

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
      <div className="flex items-center justify-between mb-4 sm:mb-8 flex-wrap gap-2">
        <div className="min-w-0">
          <div className="text-[10px] sm:text-xs uppercase tracking-[0.18em] text-slate-500">Meus atendimentos</div>
          <h1 className="font-display font-bold text-2xl sm:text-3xl md:text-4xl text-[#0B2B62] tracking-tight mt-0.5">Acompanhe seus casos</h1>
        </div>
        <Link to="/pedir-ajuda" data-testid="new-case-btn"
          className="inline-flex items-center gap-1.5 sm:gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#DC2626] text-white rounded-md text-sm font-semibold hover:bg-red-700 transition-colors">
          <PlusCircle className="w-4 h-4" /> <span className="hidden sm:inline">Novo pedido</span><span className="sm:hidden">Novo</span>
        </Link>
      </div>

      <PushToggle audience="familia" />

      {loading ? <p className="text-slate-500">Carregando...</p> :
       cases.length === 0 ? (
        // Empty state — família recém-cadastrada cai aqui. Card grande + CTA
        // vermelho gigante pra empurrar direto pra triagem sem hesitação.
        <div className="bg-gradient-to-br from-white via-red-50/40 to-white border-2 border-red-200 rounded-2xl p-8 sm:p-12 text-center relative overflow-hidden" data-testid="mycases-empty-cta">
          <div className="absolute -top-8 -right-8 w-40 h-40 bg-red-100/60 rounded-full blur-3xl pointer-events-none" />
          <div className="relative">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-2xl bg-red-600 text-white mx-auto shadow-lg shadow-red-500/30">
              <PlusCircle className="w-8 h-8" strokeWidth={2} />
            </div>
            <h3 className="font-display font-bold text-2xl sm:text-3xl text-[#0B2B62] mt-5 tracking-tight">
              Bem-vindo(a) ao RAF Direct
            </h3>
            <p className="text-sm sm:text-base text-slate-600 mt-2 max-w-md mx-auto leading-relaxed">
              Para começarmos a te ajudar, faça a triagem inteligente
              (leva cerca de <strong>3 minutos</strong>). Escolha o objetivo abaixo:
            </p>
            {/* Iter 68.19 — 4 intents para a triagem. Mesma tela de triagem,
                query param `intent` grava a intenção no caso pra o consultor
                calibrar o atendimento (só orientação vs internação vs orçamento). */}
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 gap-3 max-w-2xl mx-auto text-left" data-testid="mycases-intent-picker">
              <Link to="/pedir-ajuda?intent=triagem"
                    data-testid="mycases-intent-triagem"
                    className="p-4 rounded-lg border-2 border-red-500 bg-red-50 hover:bg-red-100 transition-all">
                <div className="text-[10px] uppercase tracking-widest text-red-700 font-black">Emergência</div>
                <div className="font-display font-bold text-[#0B2B62] text-base mt-1">Triagem urgente</div>
                <div className="text-xs text-slate-600 mt-1">Consultor em até 15 min · situação de crise</div>
              </Link>
              <Link to="/pedir-ajuda?intent=orientacao"
                    data-testid="mycases-intent-orientacao"
                    className="p-4 rounded-lg border-2 border-slate-300 bg-white hover:border-[#0B2B62] hover:bg-blue-50 transition-all">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Sem internação</div>
                <div className="font-display font-bold text-[#0B2B62] text-base mt-1">Só orientação</div>
                <div className="text-xs text-slate-600 mt-1">Entender opções e próximos passos</div>
              </Link>
              <Link to="/pedir-ajuda?intent=internacao"
                    data-testid="mycases-intent-internacao"
                    className="p-4 rounded-lg border-2 border-slate-300 bg-white hover:border-[#0B2B62] hover:bg-blue-50 transition-all">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Clínica presencial</div>
                <div className="font-display font-bold text-[#0B2B62] text-base mt-1">Com internação</div>
                <div className="text-xs text-slate-600 mt-1">Buscar Unidade Parceira RAF</div>
              </Link>
              <Link to="/pedir-ajuda?intent=orcamento"
                    data-testid="mycases-intent-orcamento"
                    className="p-4 rounded-lg border-2 border-slate-300 bg-white hover:border-[#0B2B62] hover:bg-blue-50 transition-all">
                <div className="text-[10px] uppercase tracking-widest text-slate-500 font-black">Comparar preços</div>
                <div className="font-display font-bold text-[#0B2B62] text-base mt-1">Só orçamento</div>
                <div className="text-xs text-slate-600 mt-1">Receber propostas de clínicas em 24h</div>
              </Link>
              <Link to="/pedir-ajuda?intent=consulta"
                    data-testid="mycases-intent-consulta"
                    className="p-4 rounded-lg border-2 border-violet-300 bg-violet-50 hover:border-violet-500 hover:bg-violet-100 transition-all sm:col-span-2">
                <div className="text-[10px] uppercase tracking-widest text-violet-700 font-black">Consulta com profissional</div>
                <div className="font-display font-bold text-[#0B2B62] text-base mt-1">Agendar consulta com profissional RAF</div>
                <div className="text-xs text-slate-600 mt-1">Triagem inteligente → consultor sugere o profissional ideal e agenda pela agenda dele</div>
              </Link>
            </div>
            <p className="text-xs text-slate-400 mt-4">
              Suas respostas são criptografadas · Consultor RAF em contato logo depois
            </p>
          </div>
        </div>
       ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          {cases.map((c) => (
            <div key={c.id} data-testid={`case-card-${c.id}`}
              className="bg-white border border-slate-200 rounded-xl p-4 hover:border-[#0B2B62] transition-colors">
              {/* Header do card: status em destaque no mobile */}
              <div className="flex items-center justify-between gap-2 mb-2">
                <span className="text-[10px] font-mono text-slate-500 uppercase tracking-wider truncate">{c.protocol}</span>
                <RiskBadge level={c.risk_level} size="sm" />
              </div>
              {/* Nome + cidade em 1 linha compacta */}
              <div className="min-w-0">
                <h3 className="font-display font-bold text-base sm:text-lg text-[#0B2B62] truncate">{c.patient_name}, {c.patient_age}</h3>
                <p className="text-xs sm:text-sm text-slate-600 truncate">{c.city} · {c.substance}</p>
              </div>
              {/* Status em destaque — o que a família mais quer ver */}
              <div className="mt-3 flex items-center gap-2">
                <span className="flex-1 text-xs font-semibold text-[#0B2B62] bg-blue-50 px-2.5 py-1.5 rounded-md truncate">
                  {STATUS_LABEL[c.status] || c.status}
                </span>
                <button onClick={() => openReview(c)} data-testid={`case-rate-${c.id}`}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-amber-300 text-amber-500 hover:bg-amber-50"
                  aria-label="Avaliar atendimento" title="Avaliar atendimento">
                  <Star className="w-5 h-5" />
                </button>
                <Link to={`/caso/${c.id}`} data-testid={`case-open-${c.id}`}
                  className="inline-flex items-center justify-center w-9 h-9 rounded-md bg-[#0B2B62] text-white hover:bg-slate-800"
                  aria-label="Abrir caso completo">
                  <ChevronRight className="w-5 h-5" />
                </Link>
              </div>
              {/* Botão de contato direto — mostra apenas no mobile */}
              <div className="mt-3 grid grid-cols-2 gap-2 sm:hidden">
                <a href={`tel:190`} data-testid={`case-emergency-${c.id}`}
                  className="inline-flex items-center justify-center gap-1.5 px-2 py-1.5 border border-red-300 text-red-600 rounded-md text-xs font-semibold">
                  <Phone className="w-3.5 h-3.5" /> Emergência 190
                </a>
                <a href={`https://wa.me/${rafSupportWa.replace(/[^0-9]/g,"")}?text=${encodeURIComponent(`Preciso de ajuda no caso ${c.protocol}`)}`}
                  target="_blank" rel="noreferrer" data-testid={`case-wa-${c.id}`}
                  onClick={() => trackWhatsApp("meus_atendimentos_help", { protocol: c.protocol })}
                  className="inline-flex items-center justify-center gap-1.5 px-2 py-1.5 border border-green-300 text-green-700 rounded-md text-xs font-semibold">
                  <MessageCircle className="w-3.5 h-3.5" /> Falar com RAF
                </a>
              </div>
              <div className="mt-2 text-[10px] text-slate-400 text-right">
                Aberto em {new Date(c.created_at).toLocaleDateString("pt-BR")}
              </div>
            </div>
          ))}
        </div>
       )}

      {reviewing && (
        <ReviewModal
          caseId={reviewing.caseObj.id}
          protocol={reviewing.caseObj.protocol}
          patientName={`${reviewing.caseObj.patient_name}, ${reviewing.caseObj.patient_age}`}
          initial={reviewing.existing}
          onClose={() => setReviewing(null)}
          onSaved={() => setReviewing(null)}
        />
      )}
    </div>
  );
};

export default MyCases;
